import { ofType } from 'redux-observable';
import { from } from 'rxjs';
import { ignoreElements, mergeMap, pluck, withLatestFrom } from 'rxjs/operators';
import { Gazetteer } from '@bridebook/toolbox/src/gazetteer';
import { getConversationId } from '@bridebook/toolbox/src/inbox/api/talkjs/get-conversation-id';
import { getSupplierTargetingBudget, isBudgetMatch } from '@bridebook/toolbox/src/isBudgetMatch';
import { CriticalWebEvents } from '@bridebook/web/lib/analyticsTypes';
import { getEnquiryFormIntents } from '@bridebook/web/lib/enquiries/selectors';
import { OnboardingAction } from '@bridebook/web/lib/onboarding/actions';
import { CreateEnquiryResponse } from '@bridebook/web/lib/onboarding/types';
import { ActionWithPayload, IEpic } from '@bridebook/web/lib/types';
import enquirySupplierTrack from '@bridebook/web/lib/utils/server-side-track/enquiry-supplier-track';
import { weddingDetailsPropertiesGeneric } from '@bridebook/web/lib/weddings/analytics';
import { getWeddingBudget, getWeddingProfileId } from '@bridebook/web/lib/weddings/selectors';

type InputAction = ActionWithPayload<CreateEnquiryResponse[]>;

/**
 * For each successfully enqueued enquiry sends the `EMAILED_SUPPLIER` event.
 */
export const sendEnquirySuccessfulAnalyticsEventEpic: IEpic<InputAction> = (action$, { state$ }) =>
  action$.pipe(
    ofType(OnboardingAction.SendEnquiriesSuccess),
    pluck<ActionWithPayload<CreateEnquiryResponse[]>, CreateEnquiryResponse[]>('payload'),
    mergeMap((enquiries) => enquiries),
    withLatestFrom(state$),
    mergeMap(([{ supplier, suppliersCount, message }, state]) => {
      const sendAnalyticsSuccessEvent = async () => {
        const weddingId = getWeddingProfileId(state);
        const intent = getEnquiryFormIntents(state);
        const budget = getWeddingBudget(state);

        const supplierCountryCode = supplier.countryCode ?? supplier.l10n?.country;
        const supplierCountryName = supplierCountryCode
          ? Gazetteer.getCountryName(supplierCountryCode)
          : undefined;
        const supplierId = supplier.id;

        return enquirySupplierTrack({
          event: CriticalWebEvents.EMAILED_SUPPLIER,
          state,
          contactedSupplier: supplier,
          category: 'Supplier Engagement',
          specificEventProps: {
            weddingId,
            contactLocation: 'onboarding',
            contactType: 'multipleEnquiry',
            contactMessage: message,
            contactMethod: 'onboardingMultiEnquiry',
            contactIntent: intent,
            budgetMatch: isBudgetMatch(budget, {
              targeting: getSupplierTargetingBudget(supplier),
              tier: supplier.tier,
              type: supplier.type,
              countryCode: supplier.countryCode,
            }),
            ...weddingDetailsPropertiesGeneric(state),
            supplierCategory: supplier.type,
            multipleEnquiryCount: suppliersCount,
            supplierCountryCode,
            supplierCountryName,
            conversationId: getConversationId([supplierId, weddingId]),
          },
        });
      };

      return from(sendAnalyticsSuccessEvent()).pipe(ignoreElements());
    }),
  );
